import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  Flame, Home, FileText, ShieldAlert, Users, FlaskConical, BarChart3, Settings, LogOut,
  AlertTriangle, Award, CalendarClock, MapPin, History, Menu, X, ClipboardCheck,
  Inbox, Repeat2, FileSearch2, Route, Plug, BookOpenCheck, CalendarCheck2, ChevronDown, ScanSearch,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_ceo-operacional/artifacts/2hvzdama_LogocomSlogan-fundoescuro.png";

const PRIMARY_NAV = [
  { to: "/", label: "Início", icon: Home, testid: "nav-dashboard", exact: true },
  { to: "/propostas", label: "Propostas", icon: FileText, testid: "nav-proposals", permission: "can_access_proposals" },
  { to: "/clientes", label: "Clientes", icon: Users, testid: "nav-clients", permission: "can_access_clients" },
  { to: "/incendio", label: "Proteção contra incêndio", icon: Flame, testid: "nav-fire", permission: "can_access_operations" },
];

const NAV_GROUPS = [
  { id: "treinamentos", label: "Treinamentos", items: [
    { to: "/treinamentos", label: "Validades e emissão", icon: Award, testid: "nav-treinamentos", exact: true, permission: "can_access_training" },
    { to: "/treinamentos/emissoes", label: "Certificados emitidos", icon: History, testid: "nav-treinamentos-emissoes", permission: "can_access_training" },
    { to: "/treinamentos/vencimentos", label: "Treinamentos a vencer", icon: CalendarClock, testid: "nav-treinamentos-vencimentos", permission: "can_access_training" },
  ] },
  {
    id: "acompanhamento",
    label: "Gestão operacional",
    items: [
      { to: "/operacoes/hoje", label: "Minha rotina", icon: CalendarCheck2, testid: "nav-today-ceo", permission: "can_access_operations" },
      { to: "/operacoes/fila", label: "Conferências e pendências", icon: Inbox, testid: "nav-operations-queue", permission: "can_access_operations" },
      { to: "/operacoes/agenda", label: "Agenda e rotas", icon: Route, testid: "nav-smart-agenda", permission: "can_access_operations" },
      { to: "/operacoes?tab=acoes", label: "Planos de ação (PGR e outros)", icon: ClipboardCheck, testid: "nav-operations", exact: true, permission: "can_access_operations" },
      { to: "/operacoes?tab=visitas", label: "Visitas técnicas", icon: MapPin, testid: "nav-visits", exact: true, permission: "can_access_operations" },
      { to: "/operacoes/obrigacoes", label: "Renovações e obrigações", icon: Repeat2, testid: "nav-obligations", permission: "can_access_operations" },
    ],
  },
  {
    id: "registros",
    label: "Documentos técnicos",
    items: [
      { to: "/documentos/pre", label: "Plano de emergência (PRE)", icon: ShieldAlert, testid: "nav-pre", adminOnly: true, permission: "can_access_documents" },
      { to: "/documentos", label: "Documentos gerados", icon: FileText, testid: "nav-docs-history", permission: "can_access_documents" },
      { to: "/operacoes/documentos", label: "Analisar documento", icon: FileSearch2, testid: "nav-document-intelligence", permission: "can_access_documents" },
      { to: "/operacoes/biblioteca", label: "Biblioteca técnica", icon: BookOpenCheck, testid: "nav-technical-library", permission: "can_access_documents" },
      { to: "/cenarios", label: "Cenários do PRE", icon: AlertTriangle, testid: "nav-cenarios", adminOnly: true, permission: "can_access_documents" },
    ],
  },
  {
    id: "administracao",
    label: "Administração",
    items: [
      { to: "/operacoes/indicadores", label: "Indicadores operacionais", icon: BarChart3, testid: "nav-efficiency-analytics", permission: "can_access_operations" },
      { to: "/cadastros/avaliacoes-fixas", label: "Avaliações fixas", icon: BarChart3, testid: "nav-fixed-evals", adminOnly: true },
      { to: "/cadastros/avaliacoes-quimicas", label: "Avaliações químicas", icon: FlaskConical, testid: "nav-chemicals", adminOnly: true },
      { to: "/cadastros/servicos", label: "Serviços e preços", icon: FileText, testid: "nav-services", adminOnly: true },
      { to: "/cadastros/cidades", label: "Cidades e deslocamento", icon: MapPin, testid: "nav-cidades", adminOnly: true },
      { to: "/integracoes", label: "Integrações", icon: Plug, testid: "nav-integrations", permission: "can_access_operations" },
      { to: "/mcp/diagnostico", label: "Diagnóstico da conexão MCP", icon: ScanSearch, testid: "nav-mcp-log", adminOnly: true },
      { to: "/configuracoes", label: "Configurações", icon: Settings, testid: "nav-settings", adminOnly: true },
    ],
  },
];

const PROFILE_LABELS = {
  equipe: "Equipe completa",
  comercial: "Comercial",
  treinamentos: "Treinamentos",
  operacional: "Operacional",
  leitura: "Somente leitura",
};

const isItemActive = (pathname, item, search = "") => {
  const path = item.to.split("?")[0];
  if (!item.exact) return pathname.startsWith(path);
  if (pathname !== path) return false;
  if (path !== "/operacoes") return true;
  const tab = new URLSearchParams(search).get("tab") || "acoes";
  return item.to.includes("tab=visitas") ? tab === "visitas" : ["acoes", "manual", "importar", "hoje"].includes(tab);
};

function NavItem({ item, onNavigate }) {
  const location = useLocation();
  const Icon = item.icon;
  const active = isItemActive(location.pathname, item, location.search);

  return (
    <NavLink
      to={item.to}
      onClick={onNavigate}
      data-testid={item.testid}
      className={`group mb-0.5 flex items-center gap-3 rounded-xl px-3 py-2.5 text-[13px] transition-all duration-200 ${
        active
          ? "sidebar-link-active font-semibold text-white"
          : "text-white/58 hover:bg-white/[0.05] hover:text-white/95"
      }`}
    >
      <span className={`grid size-8 shrink-0 place-items-center rounded-lg transition-colors ${active ? "bg-primary/15 text-primary" : "text-white/45 group-hover:text-white/80"}`}>
        <Icon className="size-4" strokeWidth={1.8} />
      </span>
      <span className="min-w-0 flex-1 leading-snug">{item.label}</span>
    </NavLink>
  );
}

function SidebarContent({ onNavigate }) {
  const { logout, isAdmin, isCollaborator, userInfo, can, accessProfile, readOnly } = useAuth();
  const location = useLocation();

  const itemVisible = React.useCallback((item) => {
    if (item.adminOnly && !isAdmin) return false;
    if (item.permission && !can(item.permission)) return false;
    return true;
  }, [can, isAdmin]);

  const visiblePrimary = React.useMemo(() => PRIMARY_NAV.filter(itemVisible), [itemVisible]);
  const visibleGroups = React.useMemo(() => NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter(itemVisible),
  })).filter((group) => group.items.length > 0), [itemVisible]);

  const activeGroupId = visibleGroups.find((group) => group.items.some((item) => isItemActive(location.pathname, item, location.search)))?.id || null;
  const [openGroups, setOpenGroups] = React.useState(() => new Set(activeGroupId ? [activeGroupId] : []));

  React.useEffect(() => {
    if (!activeGroupId) return;
    setOpenGroups((prev) => {
      if (prev.has(activeGroupId)) return prev;
      const next = new Set(prev);
      next.add(activeGroupId);
      return next;
    });
  }, [activeGroupId]);

  const toggleGroup = (groupId) => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  };

  const handleLogout = () => {
    onNavigate?.();
    logout();
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="relative overflow-hidden border-b border-white/5 px-5 pb-4 pt-5">
        <div className="pointer-events-none absolute -right-12 -top-16 size-40 rounded-full bg-primary/15 blur-3xl" />
        <img src={LOGO_URL} alt="DEMoraes Consultoria" className="relative h-auto max-h-16 w-full object-contain object-left" />
        {location.pathname.startsWith("/incendio") && <div className="relative mt-3 rounded-lg border border-orange-400/20 bg-orange-400/10 px-3 py-2 text-[10px] font-semibold tracking-wider text-orange-300">PROTEÇÃO CONTRA INCÊNDIO</div>}
        {isCollaborator && (
          <div className="relative mt-3 flex flex-wrap items-center gap-1.5" data-testid="role-badge-collaborator">
            <span className="inline-flex items-center rounded-full border border-amber-400/20 bg-amber-400/10 px-2.5 py-1 text-[10px] font-mono uppercase tracking-wider text-amber-300">{PROFILE_LABELS[accessProfile] || "Colaborador"}</span>
            {readOnly && <span className="inline-flex items-center rounded-full border border-white/10 bg-white/5 px-2 py-1 text-[9px] uppercase tracking-wider text-white/55">sem edição</span>}
          </div>
        )}
      </div>

      <nav className="min-h-0 flex-1 overflow-y-auto px-3 py-3" data-testid="sidebar-nav">
        <div className="px-3 pb-1 pt-1 text-[9px] font-mono uppercase tracking-[0.17em] text-white/35">Principal</div>
        {visiblePrimary.map((item) => <NavItem key={item.to} item={item} onNavigate={onNavigate} />)}

        {visibleGroups.length > 0 && (
          <div className="mt-4 border-t border-white/[0.06] pt-3">
            <div className="px-3 pb-1 text-[9px] font-mono uppercase tracking-[0.17em] text-white/35">Áreas de trabalho</div>
            {visibleGroups.map((group) => {
              const open = openGroups.has(group.id);
              const active = group.items.some((item) => isItemActive(location.pathname, item, location.search));
              return (
                <div key={group.id} className="mb-1">
                  <button type="button" onClick={() => toggleGroup(group.id)} className={`flex w-full items-center justify-between rounded-xl px-3 py-2.5 text-left text-[12px] font-medium transition ${active ? "bg-white/[0.045] text-white/90" : "text-white/48 hover:bg-white/[0.04] hover:text-white/80"}`} aria-expanded={open} data-testid={`nav-group-${group.id}`}>
                    <span>{group.label}</span><ChevronDown className={`size-4 transition-transform ${open ? "rotate-180" : ""}`} />
                  </button>
                  {open && <div className="ml-2 border-l border-white/[0.06] pl-2 pt-1">{group.items.map((item) => <NavItem key={item.to} item={item} onNavigate={onNavigate} />)}</div>}
                </div>
              );
            })}
          </div>
        )}
      </nav>

      <div className="border-t border-white/5 p-3">
        <div className="mb-2 rounded-xl border border-white/[0.06] bg-white/[0.025] p-3">
          <div className="text-[9px] font-mono uppercase tracking-[0.14em] text-white/35">Usuário</div>
          <div className="mt-1 truncate text-xs font-medium text-white/80" data-testid="sidebar-user-name">{userInfo?.nome || (isAdmin ? "Administrador" : "Usuário")}</div>
        </div>
        <button data-testid="sidebar-logout-btn" onClick={handleLogout} className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-white/45 transition-colors hover:bg-destructive/10 hover:text-red-300"><LogOut className="size-4" /> Sair</button>
      </div>
    </div>
  );
}

export default function Sidebar() {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const location = useLocation();

  React.useEffect(() => { setMobileOpen(false); }, [location.pathname]);
  React.useEffect(() => {
    if (!mobileOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = previous; };
  }, [mobileOpen]);
  React.useEffect(() => {
    if (!mobileOpen) return undefined;
    const onKeyDown = (event) => { if (event.key === "Escape") setMobileOpen(false); };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [mobileOpen]);

  return (
    <>
      <aside className="sticky top-0 hidden h-screen w-[276px] shrink-0 border-r border-white/[0.06] bg-[hsl(154_40%_4%)] text-white lg:flex lg:flex-col" data-testid="app-sidebar"><SidebarContent /></aside>
      <button type="button" aria-label="Abrir navegação" aria-expanded={mobileOpen} onClick={() => setMobileOpen(true)} className="fixed left-3 top-3 z-[55] grid size-10 place-items-center rounded-xl border border-border/70 bg-card/90 text-foreground shadow-lg backdrop-blur-xl transition hover:bg-accent lg:hidden" data-testid="mobile-nav-trigger"><Menu className="size-5" /></button>
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" role="presentation">
          <button type="button" aria-label="Fechar navegação" onClick={() => setMobileOpen(false)} className="absolute inset-0 h-full w-full bg-black/60 backdrop-blur-sm" />
          <aside className="absolute inset-y-0 left-0 z-[60] w-[min(88vw,320px)] border-r border-white/[0.06] bg-[hsl(154_40%_4%)] text-white shadow-2xl" data-testid="mobile-sidebar">
            <button type="button" aria-label="Fechar menu" onClick={() => setMobileOpen(false)} className="absolute right-3 top-3 z-10 grid size-9 place-items-center rounded-lg border border-white/10 bg-white/5 text-white/70 transition hover:bg-white/10 hover:text-white"><X className="size-4" /></button>
            <SidebarContent onNavigate={() => setMobileOpen(false)} />
          </aside>
        </div>
      )}
    </>
  );
}

